import type Konva from 'konva';
import { useEffect, useRef, useState } from 'react';
import { Group, Image as KonvaImage, Rect, Text, Transformer } from 'react-konva';
import { toast } from 'sonner';

import type { LogoElement } from './useTextEditorStore';
import { useTextEditorStore } from './useTextEditorStore';

import { logger } from '@/utils/logger';

interface KonvaLogoNodeProps {
  element: LogoElement;
  isSelected: boolean;
  onDragMove: (
    id: string,
    x: number,
    y: number,
    width: number,
    height: number,
    node: { x: (v: number) => void; y: (v: number) => void },
  ) => void;
  onDragEndGuides: () => void;
}

export function KonvaLogoNode({
  element,
  isSelected,
  onDragMove,
  onDragEndGuides,
}: KonvaLogoNodeProps) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [loadError, setLoadError] = useState(false);
  const updateElement = useTextEditorStore((s) => s.updateElement);
  const selectElement = useTextEditorStore((s) => s.selectElement);
  const nodeRef = useRef<Konva.Image>(null);
  const trRef = useRef<Konva.Transformer>(null);

  useEffect(() => {
    setLoadError(false);
    setImage(null);
    if (!element.logoUrl) return;
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setImage(img);
    // Don't fail silently — a missing onerror left the element invisible on
    // CORS denials / 404s, with no diagnostic in the console.
    img.onerror = () => {
      logger.error('KonvaLogoNode: logo image failed to load', {
        id: element.id,
        logoUrl: element.logoUrl,
        logoVariant: element.logoVariant,
      });
      setLoadError(true);
      toast.error(
        'Logo failed to load — it may be hosted externally without CORS access. Re-upload it in Brand Kit settings.',
        { id: `logo-load-error-${element.id}` },
      );
    };
    img.src = element.logoUrl;
  }, [element.logoUrl, element.id, element.logoVariant]);

  useEffect(() => {
    if (isSelected && image && trRef.current && nodeRef.current) {
      trRef.current.nodes([nodeRef.current]);
      trRef.current.forceUpdate();
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected, image]);

  const handleDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
    onDragMove(
      element.id,
      e.target.x(),
      e.target.y(),
      element.width * element.scaleX,
      element.height * element.scaleY,
      e.target as unknown as { x: (v: number) => void; y: (v: number) => void },
    );
  };

  const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    updateElement(element.id, { x: e.target.x(), y: e.target.y() });
    onDragEndGuides();
  };

  const handleTransformEnd = (e: Konva.KonvaEventObject<Event>) => {
    const node = e.target;
    updateElement(element.id, {
      x: node.x(),
      y: node.y(),
      scaleX: node.scaleX(),
      scaleY: node.scaleY(),
      rotation: node.rotation(),
    });
  };

  if (loadError) {
    const placeholderWidth = element.width * element.scaleX;
    const placeholderHeight = element.height * element.scaleY;
    return (
      <Group
        id={element.id}
        x={element.x}
        y={element.y}
        rotation={element.rotation}
        draggable
        onClick={() => selectElement(element.id)}
        onTap={() => selectElement(element.id)}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
      >
        <Rect
          width={placeholderWidth}
          height={placeholderHeight}
          fill="rgba(0,0,0,0.06)"
          stroke="rgba(0,0,0,0.55)"
          strokeWidth={1}
          dash={[4, 4]}
          cornerRadius={4}
        />
        <Text
          text="Logo failed to load"
          width={placeholderWidth}
          height={placeholderHeight}
          fontSize={12}
          fill="rgba(0,0,0,0.65)"
          align="center"
          verticalAlign="middle"
        />
      </Group>
    );
  }

  if (!image) return null;

  return (
    <>
      <KonvaImage
        ref={nodeRef}
        id={element.id}
        image={image}
        x={element.x}
        y={element.y}
        width={element.width}
        height={element.height}
        scaleX={element.scaleX}
        scaleY={element.scaleY}
        rotation={element.rotation}
        opacity={element.opacity}
        draggable
        onClick={() => selectElement(element.id)}
        onTap={() => selectElement(element.id)}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onTransformEnd={handleTransformEnd}
      />

      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 10 || newBox.height < 10) {
              return oldBox;
            }
            return newBox;
          }}
          borderStroke="rgba(0,0,0,0.55)"
          borderStrokeWidth={1}
          borderDash={[4, 4]}
          anchorStroke="rgba(0,0,0,0.65)"
          anchorFill="#ffffff"
          anchorSize={9}
          anchorCornerRadius={4}
          rotateAnchorOffset={28}
          ignoreStroke
        />
      )}
    </>
  );
}
